import {useAuth} from '@apps-common/auth-react';
import {MapboxLayer, PlanetMosaicLayer} from '@apps-common/ui-maps';
import GeojsonLayer from '@apps-common/ui-maps/GeojsonLayer.jsx';
import {
  BASE_LAYERS,
  BASE_LAYERS_STYLES,
} from '@apps-common/ui-maps/constants.js';
import {MapThemeProvider} from '@apps-common/ui-maps/context/MapThemeProvider.jsx';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import {ThemeProvider, useTheme} from '@mui/material';
import Map from '@planet/maps/Map';
import Overlay from '@planet/maps/Overlay';
import View from '@planet/maps/View';
import {always} from 'ol/events/condition';
import DragZoom from 'ol/interaction/DragZoom';
import {defaults as defaultInteractions} from 'ol/interaction/defaults';
import {array, bool, func, number, object, string} from 'prop-types';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {connect, useSelector} from 'react-redux';

import CenterMarker from './center-marker.jsx';
import CompareTileLayers from './compare-layers.jsx';
import EditableVectorLayer from './editable-vector-layer.jsx';
import RasterTileLayers from './raster-layer.jsx';
import {
  changeMapView,
  fitExtent,
  pinMap,
  setDrawTool,
} from '@explorer/actions/map';
import {defineGeometry} from '@explorer/actions/search';
import {MAPBOX_ACCESS_TOKEN} from '@explorer/config';
import {
  ENHANCEMENT_MODE,
  MAP_DRAW_LAYERS,
  MAP_TOOLS,
  MAX_FOLDERS_ZOOM,
  MAX_ZOOM,
  TILE_MODE,
  UI_MODES,
} from '@explorer/constants';
import {isAnalytic} from '@explorer/selectors/capabilities';
import {getMosaicAOIs} from '@explorer/selectors/mosaics';
import {
  getGeometryZoomTo,
  getSearchGeometryGeoJson,
} from '@explorer/selectors/search';
import {
  getCompareSubjectsConfigs,
  getEnhanceFilters,
  getTileConfigs,
} from '@explorer/selectors/tile-groups';
import {
  LEFT_DRAWER_WIDTH,
  RIGHT_DRAWER_WIDTH,
} from '@explorer/styles/dimensions.js';
import {getExtent} from '@explorer/util/geom';
import {createGeometriesMask} from '@explorer/util/map';
import {
  footprintStyle,
  mosaicGeometryStyle,
} from '@explorer/util/map-edit-styles';

const dragZoomInteraction = new DragZoom({condition: always});

const ViewerMap = ({
  selectedBaseLayer,
  boxZoomEnabled,
  center,
  changeMapView,
  compareMethod,
  compareModeValues,
  defineGeometry,
  drawTool,
  enhanceFilters,
  enhancementOn,
  fitExtent,
  focusedItems,
  hoverFeatures,
  isAnalytic,
  isComparing,
  isLeftDrawerOpen,
  isRightDrawerOpen,
  mapNextExtent,
  mapProps,
  measureFeatures,
  mosaicAois,
  overlays,
  pinMap,
  pinnedLocation,
  quickFocusedItems,
  searchFeatures,
  searchGeometry,
  searchGeometryId,
  setDrawTool,
  setMeasureFeatures,
  setSearchFeatures,
  setTempGeometry,
  tileMode,
  zoom,
  zoomToSearchGeometry,
}) => {
  const auth = useAuth();
  const theme = useTheme();

  const viewRef = useRef(null);
  const mapRef = useRef(null);
  const [pinElement, setPinElement] = useState();

  const compareConfigs = useSelector(state =>
    getCompareSubjectsConfigs(state, auth)
  );
  const tileConfigs = useSelector(state => getTileConfigs(state, auth));

  // when the geometry changes, reproject it to map coordinates
  const [viewState, setViewState] = useState({
    center: [0, 0],
    zoom: 1,
  });

  useEffect(() => {
    setViewState({center, zoom});
  }, [center, zoom]);

  const onViewChange = useCallback(
    event => {
      const view = event.target;
      const newCenter = view.getCenter();
      const newZoom = view.getZoom();
      changeMapView({
        center: newCenter,
        zoom: newZoom,
      });
    },
    [changeMapView]
  );

  const onMapSingleClick = useCallback(
    event => {
      pinMap({coordinate: event.coordinate});
    },
    [pinMap]
  );

  const zoomToExtent = useCallback(
    extent => {
      const buffer = 16;
      const padding = [
        buffer,
        (isRightDrawerOpen ? RIGHT_DRAWER_WIDTH : 0) + buffer,
        buffer,
        (isLeftDrawerOpen ? LEFT_DRAWER_WIDTH : 0) + buffer,
      ];

      return new Promise(resolve => {
        // 100ms gives the layer a chance to redraw
        //  OL will not redraw while zooming.
        setTimeout(() => {
          viewRef?.current?.fit(extent, {
            padding,
            duration: 800,
          });
        }, 100);

        resolve();
      });
    },
    [isLeftDrawerOpen, isRightDrawerOpen]
  );

  useEffect(() => {
    if (zoomToSearchGeometry && searchGeometry) {
      const extent = getExtent(searchGeometry);
      zoomToExtent([extent.left, extent.bottom, extent.right, extent.top]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchGeometry, zoomToSearchGeometry]);

  useEffect(() => {
    if (searchGeometry) {
      setSearchFeatures([
        {
          type: 'Feature',
          properties: {},
          id: searchGeometryId,
          geometry: searchGeometry,
        },
      ]);
    } else if (searchFeatures?.length > 0 && !searchGeometry) {
      setSearchFeatures([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchGeometryId, searchGeometry]);

  useEffect(() => {
    if (mapNextExtent.length === 4) {
      zoomToExtent(mapNextExtent).then(() => {
        fitExtent([]);
      });
    }
  }, [mapNextExtent, fitExtent, zoomToExtent]);

  const onAddGeometryFeature = useCallback(
    newFeature => {
      if (newFeature) {
        setSearchFeatures([newFeature]);
        defineGeometry({geometry: newFeature.geometry});
        // reset selection
        setDrawTool({
          name: MAP_TOOLS.NONE,
          layer: MAP_DRAW_LAYERS.NONE,
          target: '',
        });
      }
    },
    [defineGeometry, setDrawTool, setSearchFeatures]
  );

  const onAddMeasureFeature = useCallback(
    newFeature => {
      setMeasureFeatures(measureFeatures.concat([newFeature]));
    },
    [measureFeatures, setMeasureFeatures]
  );

  useEffect(() => {
    const resetBoxZoom = () => {
      setDrawTool({
        name: MAP_TOOLS.NONE,
        layer: MAP_DRAW_LAYERS.NONE,
        target: '',
      });
    };
    dragZoomInteraction.on('boxend', resetBoxZoom);

    return () => {
      dragZoomInteraction.un('boxend', resetBoxZoom);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dragZoomInteraction.setActive(boxZoomEnabled);
  }, [boxZoomEnabled, setDrawTool]);

  useEffect(() => {
    if (mapRef.current) {
      let cursor = 'default';
      if (drawTool.name !== MAP_TOOLS.NONE) {
        cursor = 'crosshair';
      }
      mapRef.current.getTarget().style.cursor = cursor;
    }
  }, [drawTool]);

  const hasImagerySelected =
    !!tileConfigs[tileMode]?.rasterConfigs || compareConfigs?.length > 0;

  return (
    <>
      <Map
        controls={[]}
        onSingleClick={onMapSingleClick}
        {...mapProps}
        ref={node => {
          mapRef.current = node;
          mapProps.ref(node);
        }}
        interactions={defaultInteractions().extend([dragZoomInteraction])}
      >
        <ThemeProvider theme={theme}>
          <MapThemeProvider>
            <View
              ref={viewRef}
              center={viewState.center}
              zoom={viewState.zoom}
              options={{
                maxZoom:
                  tileMode === TILE_MODE.FOLDERS ? MAX_FOLDERS_ZOOM : MAX_ZOOM,
              }}
              onChange={onViewChange}
            />

            <MapboxLayer
              visible={selectedBaseLayer === BASE_LAYERS.DARK}
              styleUrl={BASE_LAYERS_STYLES[BASE_LAYERS.DARK].background}
              accessToken={MAPBOX_ACCESS_TOKEN}
            />

            <MapboxLayer
              visible={selectedBaseLayer === BASE_LAYERS.LIGHT}
              styleUrl={BASE_LAYERS_STYLES[BASE_LAYERS.LIGHT].background}
              accessToken={MAPBOX_ACCESS_TOKEN}
            />

            <PlanetMosaicLayer
              accessToken={auth.user.accessToken}
              visible={selectedBaseLayer === BASE_LAYERS.SATELLITE}
              style={{
                exposure: hasImagerySelected ? 0.3 : 0,
                contrast: hasImagerySelected ? -0.4 : 0,
                saturation: hasImagerySelected ? -0.45 : 0,
              }}
            />

            <RasterTileLayers
              olView={viewRef}
              enhancementOn={enhancementOn}
              clipGeometry={
                tileMode === TILE_MODE.RESULTS ? searchGeometry : null
              }
              rasterConfigs={tileConfigs[tileMode]?.rasterConfigs || []}
              numpyUrl={tileConfigs[tileMode]?.numpyUrl}
              pixelDepth={tileConfigs[tileMode]?.pixelDepth}
              isAnalytic={isAnalytic}
              enhanceFilters={enhanceFilters}
              visible={!isComparing}
              watermarkGeometry={
                tileMode === TILE_MODE.BASEMAPS
                  ? createGeometriesMask(mosaicAois)
                  : tileMode === TILE_MODE.RESULTS
                    ? searchGeometry
                    : null
              }
            />

            <CompareTileLayers
              visible={isComparing}
              compareTileConfigs={compareConfigs}
              clipGeometry={searchGeometry}
              olMapRef={mapProps.ref}
              values={compareModeValues}
              compareMethod={compareMethod}
            />

            <GeojsonLayer
              style={mosaicGeometryStyle}
              visible={tileMode === TILE_MODE.BASEMAPS}
              features={mosaicAois}
            />

            <GeojsonLayer
              style={footprintStyle}
              visible={!isComparing}
              features={focusedItems.map(item => item.geometry)}
            />

            <GeojsonLayer
              style={footprintStyle}
              visible={!isComparing}
              features={quickFocusedItems.map(item => item.geometry)}
            />

            <MapboxLayer
              key={`labels_${selectedBaseLayer}`}
              visible={overlays.labels}
              styleUrl={BASE_LAYERS_STYLES[selectedBaseLayer].labels}
              accessToken={MAPBOX_ACCESS_TOKEN}
            />

            <EditableVectorLayer
              layerName={MAP_DRAW_LAYERS.GEOMETRY}
              features={searchFeatures}
              drawTool={drawTool}
              onAddFeature={onAddGeometryFeature}
              setTempGeometry={setTempGeometry}
              visible={tileMode !== TILE_MODE.FOLDERS}
            />

            <EditableVectorLayer
              layerName={MAP_DRAW_LAYERS.MEASURE}
              features={measureFeatures}
              drawTool={drawTool}
              onAddFeature={onAddMeasureFeature}
              setTempGeometry={setTempGeometry}
            />

            <GeojsonLayer
              style="hover"
              visible={drawTool.name !== MAP_TOOLS.EDIT}
              features={hoverFeatures}
            />

            <CenterMarker
              geometry={searchGeometry}
              visible={overlays.centerMarker}
            />
            <Overlay
              element={pinElement}
              position={pinnedLocation}
              positioning="center-center"
            />
          </MapThemeProvider>
        </ThemeProvider>
      </Map>
      <GpsFixedIcon
        ref={setPinElement}
        fontSize="small"
        sx={{filter: 'drop-shadow( 0 0px 2px rgba(0, 0, 0, .3))'}}
      />
    </>
  );
};

ViewerMap.propTypes = {
  boxZoomEnabled: bool,
  center: array,
  changeMapView: func,
  compareConfigs: array,
  compareMethod: string,
  compareModeValues: object,
  compareSliderValue: number,
  defineGeometry: func,
  drawTool: object,
  enhanceFilters: object,
  enhancementOn: bool,
  fitExtent: func,
  focusedItems: array,
  hoverFeatures: array,
  isAnalytic: bool,
  isComparing: bool,
  isLeftDrawerOpen: bool,
  isRightDrawerOpen: bool,
  mapNextExtent: array,
  mapProps: object,
  measureFeatures: array,
  mosaicAois: array,
  overlays: object,
  pinMap: func,
  pinnedLocation: array,
  quickFocusedItems: array,
  searchFeatures: array,
  searchGeometry: object,
  searchGeometryId: number,
  setDrawTool: func,
  setMeasureFeatures: func,
  setSearchFeatures: func,
  setTempGeometry: func,
  tileConfigs: object,
  tileMode: string,
  zoom: number,
  zoomToSearchGeometry: bool,
  selectedBaseLayer: string,
};

const mapStateToProps = state => ({
  boxZoomEnabled: state.map.drawTool.name === MAP_TOOLS.BOX_ZOOM,
  center: state.map.center,
  // compareConfigs: getCompareSubjectsConfigs(state),
  compareMethod: state.compare.method,
  drawTool: state.map.drawTool,
  enhanceFilters: getEnhanceFilters(state),
  enhancementOn:
    state.tiles.modeSettings[state.tiles.mode].enhanceMode !==
    ENHANCEMENT_MODE.OFF,
  focusedItems: state.focusedItems,
  isAnalytic: isAnalytic(state),
  isComparing: state.ui.mode === UI_MODES.COMPARE,
  isLeftDrawerOpen: state.ui.drawers.left !== '',
  isRightDrawerOpen: state.ui.drawers.right !== '',
  mapNextExtent: state.map.fitExtent,
  mosaicAois: getMosaicAOIs(state),
  overlays: state.map.overlays,
  pinnedLocation: state.map.mapPin,
  quickFocusedItems: state.quickFocusedItems,
  searchGeomeryId: state.search.geometryId,
  searchGeometry: getSearchGeometryGeoJson(state),
  searchGeometryId: state.search.geometryId,
  tileMode: state.tiles.mode,
  zoom: state.map.zoom,
  zoomToSearchGeometry: getGeometryZoomTo(state),
  selectedBaseLayer: state.map.baseLayer,
});

const mapDispatchToProps = {
  changeMapView,
  fitExtent,
  defineGeometry,
  setDrawTool,
  pinMap,
};

export default connect(mapStateToProps, mapDispatchToProps)(ViewerMap);
